import {
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  SortingState,
  useReactTable,
  ColumnDef,
  SortingFn,
} from "@tanstack/react-table";
import { useState, useMemo } from "react";
import { HeaderDef } from "@/types/table";
import { INITIAL_ROWS } from "./useExpandTable";

export enum TableVariants {
  VIRTUALIZED = "virtualized",
  EXPANDED = "expanded",
}

export type UseTableProps<TData> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  columns: (HeaderDef<TData> | ColumnDef<TData>)[];
  variant: TableVariants;
  sortBy?: { columnId: string; desc: false };
  defaultSortingState?: SortingState;
};

const useTable = <TData extends object>({
  data,
  columns,
  variant,
  defaultSortingState = [],
}: UseTableProps<TData>) => {
  const [sorting, setSorting] = useState<SortingState>(defaultSortingState);
  const pagination =
    variant === "expanded" ? { pageSize: INITIAL_ROWS } : undefined;
  const paginationRowModel =
    variant === "expanded" ? getPaginationRowModel() : undefined;
  const sortingFns: Record<string, SortingFn<TData>> = useMemo(
    () => ({
      alphanumericNullLast: (rowA, rowB, columnId) => {
        const desc = sorting.at(0)?.desc || false;
        let rowAValue = rowA.getValue(columnId);
        let rowBValue = rowB.getValue(columnId);
        if (typeof rowAValue === "string") {
          rowAValue = rowAValue.replace("$", "").replaceAll(",", "");
        }
        if (typeof rowBValue === "string") {
          rowBValue = rowBValue.replace("$", "").replaceAll(",", "");
        }

        if (!rowAValue && !rowBValue) {
          return 0;
        }

        if (
          rowAValue === "-" ||
          rowAValue === "N/A" ||
          (!rowAValue && rowAValue !== 0 && rowAValue !== "0")
        ) {
          return desc ? -1 : 1;
        }

        if (
          rowBValue === "-" ||
          rowBValue === "N/A" ||
          (!rowBValue && rowBValue !== 0 && rowBValue !== "0")
        ) {
          return desc ? 1 : -1;
        }

        const rowANumber = Number.parseFloat(rowAValue as string);
        const rowBNumber = Number.parseFloat(rowBValue as string);
        if (Number.isNaN(rowANumber) || Number.isNaN(rowBNumber)) {
          if (typeof rowAValue === "string") {
            return rowAValue.localeCompare(rowB.getValue(columnId));
          }
        }
        return rowANumber - rowBNumber;
      },
    }),
    [sorting]
  );

  const columnVisibility = useMemo(
    () =>
      columns?.reduce(
        (cols, header) => {
          if (!header.id || !("hidden" in header)) {
            return cols;
          }
          return { ...cols, [header.id]: !header.hidden };
        },
        {} as Record<keyof ColumnDef<TData>, boolean>
      ),
    [columns]
  );

  const table = useReactTable({
    data,
    columns,
    sortingFns,
    state: {
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: paginationRowModel,
    onSortingChange: setSorting,
    initialState: {
      columnVisibility,
      pagination,
    },
  });

  return { table, sorting };
};

export default useTable;
